import { Autocomplete, Chip, FormControlLabel, MenuItem, Switch, TextField } from "@mui/material";
import { useState } from "react";
import { CatalogService } from "@services/catalog/catalogService";
import {
  CatalogDimension,
  CatalogEntityType,
  CatalogMetric,
  CatalogMetricType,
  FieldCategory,
  FIELD_CATEGORY_LABEL,
  FIELD_CATEGORY_ORDER,
  FIELD_CATEGORY_TOLERANCE,
  ItemUsage,
} from "@models/catalog.model";
import { FormDialog } from "./FormDialog";
import { UsagePanel, rekeyNotice, rewriteNotice } from "./usage";

export function MetricDialog({
  verticalId, entityTypes, metricTypes, dimensions, presetEntityTypeId, usage, initial, onClose, onSaved,
}: {
  verticalId: number;
  entityTypes: CatalogEntityType[];
  metricTypes: CatalogMetricType[];
  dimensions: CatalogDimension[]; // governed dimension_keys options
  presetEntityTypeId?: number; // when "+ metric" is clicked from within an entity type
  usage?: ItemUsage; // what an attr_key change would break (MPN-1781)
  initial: CatalogMetric | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const svc = new CatalogService();
  // Only base/global types + this vertical's own domain types are assignable.
  const typeOptions = metricTypes.filter((t) => t.vertical_id == null || t.vertical_id === verticalId);
  const dimensionOptions = dimensions.map((d) => d.code);

  // In create mode, prefill the entity type (the one being authored) and a value type so the
  // form is savable in one field; the user still sees + can change both dropdowns. Default the
  // value type to the base String primitive (a safe neutral) — NOT the alphabetically-first type,
  // which could be a junk domain type like a bare "age".
  const defaultValueType =
    typeOptions.find((t) => t.vertical_id == null && t.base_kind === "string") ??
    typeOptions.find((t) => t.vertical_id == null);
  const [entityTypeId, setEntityTypeId] = useState<number | "">(
    initial?.entity_type_def_id ?? presetEntityTypeId ?? entityTypes[0]?.id ?? "",
  );
  const [attrKey, setAttrKey] = useState(initial?.attr_key ?? "");
  const [valueTypeId, setValueTypeId] = useState<number | "">(
    initial?.value_type_id ?? defaultValueType?.id ?? "",
  );
  const [displayName, setDisplayName] = useState(initial?.display_name ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [category, setCategory] = useState<FieldCategory>(initial?.category ?? "targeting");
  const [sample, setSample] = useState(initial?.sample ?? "");
  const [dimensionKeys, setDimensionKeys] = useState<string[]>(initial?.dimension_keys ?? []);
  const [isSecure, setIsSecure] = useState(initial?.is_secure ?? false);
  const [searchable, setSearchable] = useState(initial?.searchable ?? false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // An attr_key rename fans out to values, projections, audience rules and rule text (MPN-1493),
  // so it informs rather than blocks. Moving the metric to another entity type is NOT a rename:
  // values are keyed by (entity_type, attr_key) and stay behind, so that is refused (MPN-1781).
  const renaming = !!initial && attrKey !== initial.attr_key;
  const moving = !!initial && entityTypeId !== initial.entity_type_def_id;
  const inUse = !!usage && usage.tier !== "unreferenced";
  const blockedReason =
    moving && inUse
      ? `Moving a metric to another entity type is not a rename; its data stays keyed to ${initial.entity_type_code}. Create the metric on the new type instead.`
      : undefined;
  const notice =
    renaming && usage?.tier === "rules"
      ? rewriteNotice(usage, `${initial.entity_type_code}.${attrKey}`).replace(
          "will be rewritten to", `will be rewritten from ${initial.entity_type_code}.${initial.attr_key} to`,
        )
      : undefined;
  const footer = renaming && inUse && usage.data.length ? rekeyNotice(usage) : undefined;

  const save = async () => {
    if (entityTypeId === "" || valueTypeId === "") return;
    setSaving(true);
    setError(null);
    const payload = {
      entity_type_def_id: entityTypeId,
      attr_key: attrKey,
      value_type_id: valueTypeId,
      display_name: displayName,
      description,
      category,
      sample,
      dimension_keys: dimensionKeys,
      is_secure: isSecure,
      searchable,
    };
    try {
      if (initial) await svc.updateMetric(initial.id, payload);
      else await svc.createMetric(payload);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormDialog
      open
      title={initial ? "Edit metric" : "New metric"}
      onClose={onClose}
      onSave={save}
      saving={saving}
      error={error}
      canSave={!!attrKey.trim() && entityTypeId !== "" && valueTypeId !== "" && !blockedReason}
    >
      <TextField select label="Entity type" value={entityTypeId}
        onChange={(e) => setEntityTypeId(e.target.value === "" ? "" : Number(e.target.value))}
        size="small">
        {entityTypes.map((t) => <MenuItem key={t.id} value={t.id}>{t.name} ({t.code})</MenuItem>)}
      </TextField>
      {/* The key is spliced into conversion-rule text as `<entity>.<attr_key>`, so it has to lex as one
          identifier — same failure as `dimension.Enrollment Month` in MPN-1776 (MPN-1782). */}
      <TextField label="Attribute key" value={attrKey}
        onChange={(e) => setAttrKey(e.target.value.toLowerCase().replace(/\s+/g, "_"))}
        helperText="Lowercase letters, digits and underscores, e.g. is_registered. Copied into data, audiences and rules; a change is carried to all of them." size="small" autoFocus />
      {(renaming || moving) && usage && <UsagePanel usage={usage} blockedReason={blockedReason} notice={notice} footer={footer} />}
      <TextField select label="Value type" value={valueTypeId}
        onChange={(e) => setValueTypeId(e.target.value === "" ? "" : Number(e.target.value))}
        size="small">
        {typeOptions.map((t) => (
          <MenuItem key={t.id} value={t.id}>
            {t.name} ({t.base_kind}){t.vertical_id == null ? " · base" : ""}
          </MenuItem>
        ))}
      </TextField>
      <TextField label="Display name" value={displayName} onChange={(e) => setDisplayName(e.target.value)}
        helperText="A label only — always safe to change" size="small" />
      <TextField label="Description" value={description} onChange={(e) => setDescription(e.target.value)}
        helperText="The metric's meaning — helps authors recognize it before duplicating" size="small" multiline minRows={2} />
      <TextField select label="Category" value={category}
        onChange={(e) => setCategory(e.target.value as FieldCategory)}
        helperText={FIELD_CATEGORY_TOLERANCE[category]} size="small">
        {FIELD_CATEGORY_ORDER.map((c) => (
          <MenuItem key={c} value={c}>{FIELD_CATEGORY_LABEL[c]}</MenuItem>
        ))}
      </TextField>
      <TextField label="Sample value" value={sample} onChange={(e) => setSample(e.target.value)}
        helperText="A canonical example for the customer instructions doc, e.g. 3.75, Y, 2026FA" size="small" />
      <Autocomplete
        multiple options={dimensionOptions} value={dimensionKeys}
        onChange={(_, v) => setDimensionKeys(v)}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => <Chip size="small" label={option} {...getTagProps({ index })} key={option} />)
        }
        renderInput={(params) => (
          <TextField {...params} label="Dimensions" size="small"
            helperText={dimensionOptions.length
              ? "Tracking axes this metric is keyed by (must be dimensions of this vertical)"
              : "No dimensions defined for this vertical yet"} />
        )}
      />
      <FormControlLabel control={<Switch checked={isSecure} onChange={(e) => setIsSecure(e.target.checked)} />}
        label="PII / secure (stored encrypted)" />
      <FormControlLabel control={<Switch checked={searchable} onChange={(e) => setSearchable(e.target.checked)} />}
        label="Searchable" />
    </FormDialog>
  );
}
